import { timeAgo } from "./inboxFormat";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "needs_human", label: "Needs a person" },
  { id: "ai", label: "AI replying" },
  { id: "paused", label: "Team replying" },
];

const CHANNELS = [
  { id: "all", label: "All channels" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "web", label: "Website" },
];

const PREVIEW_FROM = { AI: "AI: ", HUMAN: "Team: " };

/** Where the conversation is happening. */
export function ChannelBadge({ channel }) {
  const web = channel === "WEB";
  return (
    <span className={`mcap-inbox-channel ${web ? "is-web" : "is-whatsapp"}`}>
      <i className={`mdi ${web ? "mdi-web" : "mdi-whatsapp"}`} aria-hidden="true" />
      {web ? "Website" : "WhatsApp"}
    </span>
  );
}

function RowStatus({ c }) {
  if (c.needsHuman) return <span className="mcap-inbox-tag is-needs">Needs a person</span>;
  if (c.optedOut) return <span className="mcap-inbox-tag">Opted out</span>;
  if (c.aiPaused) return <span className="mcap-inbox-tag is-human">Team</span>;
  return <span className="mcap-inbox-tag is-ai">AI</span>;
}

export default function ConversationList({
  live,
  list,
  filter,
  onFilter,
  channel = "all",
  onChannel = () => {},
  search,
  onSearch,
  selectedId,
  onSelect,
}) {
  const rows = list.data?.data ?? [];
  const needsCount = list.data?.needsHumanCount ?? 0;

  return (
    <aside className="mcap-inbox-list" aria-label="Conversations">
      <div className="mcap-inbox-list-head">
        <label className="mcap-inbox-search">
          <i className="mdi mdi-magnify" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search name, phone, school or email"
            aria-label="Search conversations"
            maxLength={100}
          />
        </label>
        <p className={`mcap-inbox-live${live ? " is-live" : ""}`} role="status">
          {live ? "Live" : "Connecting…"}
        </p>
        <div className="mcap-lic-chips" role="group" aria-label="Show">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={filter === f.id ? "is-active" : ""}
              aria-pressed={filter === f.id}
              onClick={() => onFilter(f.id)}
            >
              {f.label}
              {f.id === "needs_human" && needsCount > 0 ? ` (${needsCount})` : ""}
            </button>
          ))}
        </div>
        <div className="mcap-lic-chips mcap-inbox-channels" role="group" aria-label="Channel">
          {CHANNELS.map((ch) => (
            <button
              key={ch.id}
              type="button"
              className={channel === ch.id ? "is-active" : ""}
              aria-pressed={channel === ch.id}
              onClick={() => onChannel(ch.id)}
            >
              {ch.label}
            </button>
          ))}
        </div>
      </div>

      {list.error && list.data && (
        <p className="mcap-inbox-reconnecting" role="status">
          Connection lost. Retrying…
        </p>
      )}

      {list.error && !list.data && (
        <div className="mcap-lic-error-card mcap-inbox-pad" role="alert">
          <h3>Conversations could not load.</h3>
          <p>The request failed; nothing was lost. {list.error.message || ""}</p>
          <button type="button" className="mcap-lic-btn" onClick={list.refresh}>
            Try again
          </button>
        </div>
      )}

      {!list.error && list.loading && !list.data && <p className="mcap-inbox-muted mcap-inbox-pad">Loading conversations…</p>}

      {!list.error && list.data && rows.length === 0 && (
        <div className="mcap-inbox-empty mcap-inbox-pad">
          <h3>{search || filter !== "all" || channel !== "all" ? "No conversations match." : "No conversations yet."}</h3>
          <p>
            {search || filter !== "all" || channel !== "all"
              ? "Try another search or show all."
              : "When a teacher or school messages the WhatsApp number, or a website visitor shares their email in the chat, the conversation appears here."}
          </p>
        </div>
      )}

      <ul className="mcap-inbox-rows">
        {rows.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              className={`mcap-inbox-row${c.id === selectedId ? " is-selected" : ""}${c.needsHuman ? " is-needs" : ""}`}
              aria-current={c.id === selectedId ? "true" : undefined}
              onClick={() => onSelect(c.id)}
            >
              <span className="mcap-inbox-row-top">
                <strong className="mcap-inbox-row-name">{c.displayName}</strong>
                <time dateTime={c.lastMessageAt || undefined}>{timeAgo(c.lastMessageAt)}</time>
              </span>
              <span className="mcap-inbox-row-preview">
                {PREVIEW_FROM[c.previewFrom] ?? ""}
                {c.preview || "—"}
              </span>
              <span className="mcap-inbox-row-meta">
                <ChannelBadge channel={c.channel} />
                <RowStatus c={c} />
                {c.hasAccount && <span className="mcap-inbox-tag is-account">Has an account</span>}
                {c.schoolName && <span className="mcap-inbox-row-school">{c.schoolName}</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {list.data?.hasMore && <p className="mcap-inbox-muted mcap-inbox-pad">Showing the 30 most recent. Search to find older chats.</p>}
    </aside>
  );
}
